import React, { FC } from 'react';

import { LucidTheme, mqMin, mqMax } from '@lucid/theme';

import { ComponentImage } from '../../../types/generics';
import Box from '../../../components/box/box';
import Text from '../../../components/text/text';
import Heading from '../../../components/heading/heading';
import Icon from '../../../components/icon/icon';
import Card from '../../../components/card/card';
import Link from '../../../components/link/link';
import AccessibleDescription from '../../../components/accessible-description/accessible-description';

export interface ComparisonCardSectionType {
  /** Header of the section */
  fieldComparisonSectionHeader?: string;
  /** List of points made in section */
  fieldComparisonSectionList?: string[];
}

export interface ComparisonCardItemType {
  /** A title describing the card */
  fieldComparisonCardTitle?: string;
  /** A list of sections in the body of the Comparison Card */
  fieldComparisonSections?: ComparisonCardSectionType[];
  /** CTA at the bottom right of the card */
  fieldCta?: {
    title: string;
    url: {
      path: string;
    }
  };
  /** Image at the top of the card */
  fieldImage?: ComponentImage;
}

export interface ComparisonCardProps {
  /** An individual Comparison Card */
  card: ComparisonCardItemType;
};

const ComparisonCard: FC<ComparisonCardProps> = ({ card }) => {
  const cardTitle = card?.fieldComparisonCardTitle;
  const cardImage = card?.fieldImage;
  const cardSections = card?.fieldComparisonSections;
  const cardCta = card?.fieldCta;

  return (
    <Box
      css={(theme: LucidTheme) => ({
        margin: `${theme.space.s24}px 0`
      })}
    >
      <Card
        css={(theme: LucidTheme) => ({
          width: '100%',
          maxWidth: '325px',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          border: 'solid black 1.5px',
          boxShadow: `${theme.space.s8}px ${theme.space.s8}px 0px 0px ${theme.colors.coolGrey[10]}`,
          '& .image': {
            maxWidth: '275px'
          },
          [mqMax.computer]: {
            [mqMin.tabletLandscape]: {
              width: '280px',
              '& .image': {
                maxWidth: '240px'
              }
            }
          }
        })}
      >
        <Box>
          <Card.Image
            alt={cardImage?.imageAlt || ''}
            css={(theme: LucidTheme) => ({
              padding: `${theme.space.s64}px 0`
            })}
            src={cardImage?.imageSrc || ''}
          />
          <div
            css={(theme: LucidTheme) => ({
              height: '7px',
              width: '100%',
              backgroundColor: theme.colors.coolGrey[100]
            })}
          />
          {cardSections?.map((section) => (
            <Box
              key={section.fieldComparisonSectionHeader}
              css={(theme: LucidTheme) => ({
                margin: theme.space.s32,
                marginTop: theme.space.s24,
                [mqMax.computer]: {
                  [mqMin.tabletLandscape]: {
                    margin: '20px'
                  }
                }
              })}
            >
              <Heading as="h2" variant="headlineM">
                {section.fieldComparisonSectionHeader}
              </Heading>
              <ul css={{ margin: 0 }}>
                {section?.fieldComparisonSectionList?.map((listItem) => (
                  <li
                    key={listItem}
                    css={(theme: LucidTheme) => ({
                      display: 'flex',
                      margin: `${theme.space.s16}px 0`
                    })}
                  >
                    <Icon
                      css={{
                        flex: '0 0 auto',
                        marginTop: '2px',
                        marginRight: '12px'
                      }}
                      type="Checkmark"
                    />
                    <Text css={{ margin: 0 }}>
                      {listItem}
                    </Text>
                  </li>
                ))}
              </ul>
            </Box>
          ))}
        </Box>
        <AccessibleDescription id={`${cardTitle}-id`} SRText={`Go to ${cardTitle} page`} />
        <Link
          aria-describedby={`${cardTitle}-id`}
          css={(theme: LucidTheme) => ({
            alignSelf: 'end',
            margin: theme.space.s16
          })}
          to={cardCta?.url?.path}
        >
          {cardCta?.title}
        </Link>
      </Card>
    </Box>
  );
};

export default ComparisonCard;
export { ComparisonCard };
